import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { useSystemSettings } from '../../lib/SystemSettingsContext';
import { pentestOrchestratorService, OrchestratorSession, OrchestratorStep } from '../../lib/pentestOrchestratorService';
import { useWebSocket } from '../../lib/useWebSocket';

export const PentestOrchestratorPage: React.FC = () => {
  const navigate = useNavigate();
  const { settings } = useSystemSettings();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  // WebSocket for real-time updates
  const { isConnected, messages, toolUpdates, connectionError, clearMessages, getToolOutput } = useWebSocket();
  
  // Form state
  const [target, setTarget] = useState('');
  const [goal, setGoal] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  
  // Session state
  const [currentSession, setCurrentSession] = useState<OrchestratorSession | null>(null);
  const [sessions, setSessions] = useState<OrchestratorSession[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Real-time execution state
  const [liveOutput, setLiveOutput] = useState<Record<string, string>>({});
  const [executingTools, setExecutingTools] = useState<Set<string>>(new Set());
  const [completedTools, setCompletedTools] = useState<Record<string, { output: string; summary: string; duration: number }>>({});
  
  // Error state
  const [error, setError] = useState<string | null>(null);

  // Manual tool testing state
  const [manualTool, setManualTool] = useState('subfinder');
  const [manualTarget, setManualTarget] = useState('example.com');
  const [manualResult, setManualResult] = useState<string>('');
  const [manualError, setManualError] = useState<string>('');
  const [manualLoading, setManualLoading] = useState(false);

  const allTools = [
    'subfinder', 'httpx', 'nuclei', 'naabu', 'amass', 'nmap', 'dirsearch',
    'llm_screenshot_classifier', 'cai_custom_xss_tool', 'scrape_content', 'extract_keywords', 'ffuf_fuzz', 'recollapse_fuzz', 'scrape_payloads',
    'google_dorking', 'github_dorking', 'asn_lookup', 'crtsh_lookup', 'censys_mapping'
  ];

  // Check authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuthenticated(true);
        setUserId(user.uid);
        loadSessions(user.uid);
      } else {
        navigate('/signin');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Handle sign out
  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      } else {
        setError("Authentication service unavailable");
      }
    } catch (error) {
      console.error('Error signing out:', error);
      setError("Failed to sign out. Please try again.");
    }
  };

  // Handle WebSocket messages for real-time updates
  useEffect(() => {
    try {
      console.log('Processing messages:', messages.length, messages); // Debug log
      messages.forEach(message => {
        console.log('WebSocket message received:', message); // Debug log
        switch (message.type) {
          case 'tool_start':
            console.log('Tool started:', message.data.tool);
            setExecutingTools(prev => new Set([...prev, message.data.tool]));
            setLiveOutput(prev => ({ ...prev, [message.data.tool]: '' }));
            break;
            
          case 'tool_output':
            console.log('Tool output:', message.data.tool, message.data.output);
            setLiveOutput(prev => ({
              ...prev,
              [message.data.tool]: (prev[message.data.tool] || '') + message.data.output
            }));
            break;
            
          case 'tool_complete':
            console.log('Tool completed:', message.data.tool);
            // Use output from the message or fall back to live output
            const finalOutput = message.data.output || liveOutput[message.data.tool] || '';
            console.log('Final output for', message.data.tool, ':', finalOutput);
            const summary = generateToolSummary(message.data.tool, finalOutput);
            setCompletedTools(prev => ({
              ...prev,
              [message.data.tool]: {
                output: finalOutput,
                summary,
                duration: Date.now() - (message.data.startTime || Date.now())
              }
            }));
            setExecutingTools(prev => {
              const newSet = new Set(prev);
              newSet.delete(message.data.tool);
              return newSet;
            });
            // Don't clear live output - keep it for completed tools
            break;
            
          case 'tool_error':
            console.log('Tool error:', message.data.tool);
            const errorOutput = liveOutput[message.data.tool] || '';
            setCompletedTools(prev => ({
              ...prev,
              [message.data.tool]: {
                output: errorOutput,
                summary: `Error: ${message.data.error || 'Tool execution failed'}`,
                duration: Date.now() - (message.data.startTime || Date.now())
              }
            }));
            setExecutingTools(prev => {
              const newSet = new Set(prev);
              newSet.delete(message.data.tool);
              return newSet;
            });
            break;
        }
      });
    } catch (error) {
      console.error('Error processing WebSocket messages:', error);
    }
  }, [messages, liveOutput]);

  // Generate tool summary based on output
  const generateToolSummary = (tool: string, output: string): string => {
    if (!output || !output.trim()) {
      return 'Tool executed successfully (no output)';
    }
    
    switch (tool) {
      case 'subfinder':
        const subdomains = output.trim().split('\n').filter(line => line.trim()).length;
        return `Found ${subdomains} subdomain${subdomains !== 1 ? 's' : ''}`;
        
      case 'httpx':
        const urls = output.trim().split('\n').filter(line => line.trim()).length;
        return `Found ${urls} live URL${urls !== 1 ? 's' : ''}`;
        
      case 'nuclei':
        const vulnerabilities = output.match(/\[([^\]]+)\]/g)?.length || 0;
        return `Found ${vulnerabilities} potential vulnerability${vulnerabilities !== 1 ? 'ies' : 'y'}`;
        
      case 'nmap':
        const ports = output.match(/portid="(\d+)"/g)?.length || 0;
        return `Found ${ports} open port${ports !== 1 ? 's' : ''}`;
        
      default:
        const lines = output.trim().split('\n').filter(line => line.trim()).length;
        return `Generated ${lines} line${lines !== 1 ? 's' : ''} of output`;
    }
  };

  // Format tool output for better readability
  const formatToolOutput = (tool: string, output: string): React.ReactNode => {
    if (!output || !output.trim()) {
      return <span className="text-gray-500 italic">No output</span>;
    }

    try {
      switch (tool) {
        case 'httpx':
          return formatHttpxOutput(output);
        case 'subfinder':
          return formatSubfinderOutput(output);
        case 'nuclei':
          return formatNucleiOutput(output);
        case 'nmap':
          return formatNmapOutput(output);
        case 'naabu':
          return formatNaabuOutput(output);
        case 'amass':
          return formatAmassOutput(output);
        case 'dirsearch':
          return formatDirsearchOutput(output);
        case 'llm_screenshot_classifier':
        case 'cai_custom_xss_tool':
          return formatPythonToolOutput(output);
        case 'google_dorking':
          return formatGoogleDorkingOutput(output);
        case 'github_dorking':
          return formatGithubDorkingOutput(output);
        default:
          return formatGenericOutput(output);
      }
    } catch (error) {
      console.error('Error formatting output:', error);
      return formatGenericOutput(output);
    }
  };

  // Format httpx JSON output
  const formatHttpxOutput = (output: string): React.ReactNode => {
    const lines = output.trim().split('\n').filter(line => line.trim());
    
    return (
      <div className="space-y-3">
        {lines.map((line, index) => {
          try {
            const data = JSON.parse(line);
            return (
              <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-semibold text-blue-800 mb-2">🔗 URL Information</div>
                    <div className="space-y-1">
                      <div><span className="font-medium">URL:</span> <span className="text-blue-600 font-mono">{data.url}</span></div>
                      <div><span className="font-medium">Scheme:</span> <Badge variant="outline" className="text-xs">{data.scheme}</Badge></div>
                      <div><span className="font-medium">Port:</span> <Badge variant="outline" className="text-xs">{data.port}</Badge></div>
                      <div><span className="font-medium">Status:</span> <Badge variant={data.status_code >= 400 ? "destructive" : "default"} className="text-xs">{data.status_code}</Badge></div>
                      <div><span className="font-medium">Title:</span> <span className="text-gray-700">{data.title}</span></div>
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-blue-800 mb-2">📊 Technical Details</div>
                    <div className="space-y-1">
                      <div><span className="font-medium">Content Type:</span> <span className="text-gray-700">{data.content_type}</span></div>
                      <div><span className="font-medium">Content Length:</span> <span className="text-gray-700">{data.content_length} bytes</span></div>
                      <div><span className="font-medium">Response Time:</span> <span className="text-gray-700">{data.time}</span></div>
                      <div><span className="font-medium">Words:</span> <span className="text-gray-700">{data.words}</span></div>
                      <div><span className="font-medium">Lines:</span> <span className="text-gray-700">{data.lines}</span></div>
                    </div>
                  </div>
                </div>
                {data.tech && data.tech.length > 0 && (
                  <div className="mt-3">
                    <div className="font-semibold text-blue-800 mb-1">🛠️ Technologies</div>
                    <div className="flex flex-wrap gap-1">
                      {data.tech.map((tech: string, techIndex: number) => (
                        <Badge key={techIndex} variant="secondary" className="text-xs">{tech}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {data.a && data.a.length > 0 && (
                  <div className="mt-3">
                    <div className="font-semibold text-blue-800 mb-1">🌐 IP Addresses (IPv4)</div>
                    <div className="flex flex-wrap gap-1">
                      {data.a.slice(0, 5).map((ip: string, ipIndex: number) => (
                        <Badge key={ipIndex} variant="outline" className="text-xs font-mono">{ip}</Badge>
                      ))}
                      {data.a.length > 5 && <Badge variant="outline" className="text-xs">+{data.a.length - 5} more</Badge>}
                    </div>
                  </div>
                )}
              </div>
            );
          } catch {
            return (
              <div key={index} className="bg-gray-50 p-2 rounded border">
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{line}</pre>
              </div>
            );
          }
        })}
      </div>
    );
  };

  // Format subfinder output
  const formatSubfinderOutput = (output: string): React.ReactNode => {
    const subdomains = output.trim().split('\n').filter(line => line.trim());
    
    return (
      <div className="space-y-2">
        <div className="font-semibold text-green-800 mb-2">🔍 Discovered Subdomains ({subdomains.length})</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {subdomains.map((subdomain, index) => (
            <div key={index} className="bg-green-50 border border-green-200 rounded p-2">
              <span className="text-sm font-mono text-green-700">{subdomain}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Format nuclei output
  const formatNucleiOutput = (output: string): React.ReactNode => {
    const lines = output.trim().split('\n').filter(line => line.trim());
    
    return (
      <div className="space-y-2">
        <div className="font-semibold text-red-800 mb-2">🚨 Vulnerability Scan Results ({lines.length})</div>
        {lines.map((line, index) => {
          // Try to extract severity and vulnerability info
          const severityMatch = line.match(/\[(critical|high|medium|low|info)\]/i);
          const severity = severityMatch ? severityMatch[1].toLowerCase() : 'info';
          
          return (
            <div key={index} className={`border rounded p-2 ${
              severity === 'critical' ? 'bg-red-50 border-red-300' :
              severity === 'high' ? 'bg-orange-50 border-orange-300' :
              severity === 'medium' ? 'bg-yellow-50 border-yellow-300' :
              severity === 'low' ? 'bg-blue-50 border-blue-300' :
              'bg-gray-50 border-gray-300'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={
                  severity === 'critical' ? 'destructive' :
                  severity === 'high' ? 'destructive' :
                  severity === 'medium' ? 'secondary' :
                  'outline'
                } className="text-xs">
                  {severity.toUpperCase()}
                </Badge>
              </div>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{line}</pre>
            </div>
          );
        })}
      </div>
    );
  };

  // Format nmap output
  const formatNmapOutput = (output: string | any): React.ReactNode => {
    // If output is a string, try to parse as JSON array (from backend)
    let parsed = null;
    if (typeof output === 'string') {
      try {
        parsed = JSON.parse(output);
      } catch {
        parsed = null;
      }
    } else if (Array.isArray(output)) {
      parsed = output;
    }

    // If parsed is an array of port objects, render as table
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].port !== undefined) {
      return (
        <div className="space-y-2">
          <div className="font-semibold text-purple-800 mb-2">🔌 Port Scan Results</div>
          <table className="min-w-full text-xs border border-purple-200 rounded">
            <thead className="bg-purple-100">
              <tr>
                <th className="px-2 py-1 border">Port</th>
                <th className="px-2 py-1 border">Protocol</th>
                <th className="px-2 py-1 border">State</th>
                <th className="px-2 py-1 border">Service</th>
              </tr>
            </thead>
            <tbody>
              {parsed.map((row, idx) => (
                <tr key={idx} className="even:bg-purple-50">
                  <td className="px-2 py-1 border font-mono">{row.port}</td>
                  <td className="px-2 py-1 border font-mono">{row.protocol}</td>
                  <td className="px-2 py-1 border font-mono">{row.state}</td>
                  <td className="px-2 py-1 border font-mono">{row.service}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Fallback: show raw XML
    const lines = (typeof output === 'string' ? output : '').trim().split('\n').filter(line => line.trim());
    return (
      <div className="space-y-2">
        <div className="font-semibold text-purple-800 mb-2">🔌 Port Scan Results (Raw XML)</div>
        <div className="bg-purple-50 border border-purple-200 rounded p-3">
          <pre className="text-xs text-purple-700 whitespace-pre-wrap font-mono">{output}</pre>
        </div>
      </div>
    );
  };

  // Format naabu output
  const formatNaabuOutput = (output: string): React.ReactNode => {
    if (output.includes('Python dependencies missing for dirsearch')) {
      return (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
          <strong>Error:</strong> Python dependencies missing for dirsearch.<br />
          <span>Run <code>python install-python-deps.py</code> or <code>pip install -r tools/dirsearch/requirements.txt</code> in your project directory.</span>
        </div>
      );
    }
    let lines: any[] = [];
    try {
      lines = output.trim().split('\n').filter(line => line.trim()).map(line => JSON.parse(line));
    } catch {
      // If parsing fails, show a user-friendly message
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-xs text-yellow-700">
          <strong>No open ports found or output was not valid JSON.</strong>
        </div>
      );
    }
    if (!lines.length) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-xs text-yellow-700">
          <strong>No open ports found.</strong>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <div className="font-semibold text-indigo-800 mb-2">🔍 Port Discovery ({lines.length})</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {lines.map((data, index) => (
            <div key={index} className="bg-indigo-50 border border-indigo-200 rounded p-2">
              <div className="text-sm font-mono text-indigo-700">{data.port || JSON.stringify(data)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Format amass output
  const formatAmassOutput = (output: string): React.ReactNode => {
    const lines = output.trim().split('\n').filter(line => line.trim());
    
    return (
      <div className="space-y-2">
        <div className="font-semibold text-cyan-800 mb-2">🌐 Amass Enumeration ({lines.length})</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {lines.map((line, index) => (
            <div key={index} className="bg-cyan-50 border border-cyan-200 rounded p-2">
              <span className="text-sm font-mono text-cyan-700">{line}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Format dirsearch output
  const formatDirsearchOutput = (output: string): React.ReactNode => {
    return (
      <div className="space-y-2">
        <div className="font-semibold text-teal-800 mb-2">📁 Directory Discovery</div>
        <div className="bg-teal-50 border border-teal-200 rounded p-3">
          <pre className="text-xs text-teal-700 whitespace-pre-wrap font-mono">{output}</pre>
        </div>
      </div>
    );
  };

  // Format Python tool output
  const formatPythonToolOutput = (output: string): React.ReactNode => {
    return (
      <div className="space-y-2">
        <div className="font-semibold text-orange-800 mb-2">🤖 AI Analysis Results</div>
        <div className="bg-orange-50 border border-orange-200 rounded p-3">
          <pre className="text-xs text-orange-700 whitespace-pre-wrap font-mono">{output}</pre>
        </div>
      </div>
    );
  };

  // Format Google Dorking output
  const formatGoogleDorkingOutput = (output: string): React.ReactNode => {
    let data: any = null;
    try {
      data = JSON.parse(output);
    } catch {
      // If not JSON, fallback
      return <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{output}</pre>;
    }
    if (!Array.isArray(data)) {
      data = [data];
    }
    // Extract search results from each response
    const allResults = data.flatMap((item: any) =>
      item.items ? item.items : []
    );
    if (allResults.length === 0) {
      return <div className="text-gray-500 italic">No search results found.</div>;
    }
    return (
      <div className="space-y-2">
        <div className="font-semibold text-blue-800 mb-2">🔎 Google Dorking Results ({allResults.length})</div>
        <ul className="space-y-1">
          {allResults.map((result: any, idx: number) => (
            <li key={idx} className="bg-blue-50 border border-blue-200 rounded p-2">
              <a href={result.link} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline font-mono text-xs">{result.title || result.link}</a>
              {result.snippet && <div className="text-xs text-gray-700 mt-1">{result.snippet}</div>}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Format GitHub Dorking output
  const formatGithubDorkingOutput = (output: string): React.ReactNode => {
    let data: any = null;
    try {
      data = JSON.parse(output);
    } catch {
      return <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{output}</pre>;
    }
    // GitHub API returns an array of results per query
    const allResults = data.flatMap((item: any) =>
      item.items ? item.items : []
    );
    if (allResults.length === 0) {
      return <div className="text-gray-500 italic">No GitHub code results found.</div>;
    }
    return (
      <div className="space-y-2">
        <div className="font-semibold text-gray-800 mb-2">🐙 GitHub Dorking Results ({allResults.length})</div>
        <ul className="space-y-1">
          {allResults.map((result: any, idx: number) => (
            <li key={idx} className="bg-gray-50 border border-gray-200 rounded p-2">
              <a href={result.html_url} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline font-mono text-xs">{result.name || result.html_url}</a>
              {result.repository && <div className="text-xs text-gray-700 mt-1">Repo: {result.repository.full_name}</div>}
              {result.path && <div className="text-xs text-gray-500">Path: {result.path}</div>}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Format generic output
  const formatGenericOutput = (output: string): React.ReactNode => {
    if (output.includes('Python dependencies missing')) {
      return (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
          <strong>Error:</strong> Python dependencies missing for this tool.<br />
          <span>Run <code>python install-python-deps.py</code> or install required packages with <code>pip install -r ai4eh/workspace/requirements.txt</code> or check the tool's README for details.</span>
        </div>
      );
    }
    if (output.includes('Tool ffuf not found in PATH')) {
      return (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
          <strong>Error:</strong> ffuf is not installed or not in your PATH.<br />
          <span>Download ffuf from <a href="https://github.com/ffuf/ffuf/releases" target="_blank" rel="noopener noreferrer" className="underline text-blue-700">ffuf releases</a> and add it to your system PATH. After installing, restart the orchestrator backend.</span>
        </div>
      );
    }
    return (
      <div className="bg-gray-50 border border-gray-200 rounded p-3">
        <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{output}</pre>
      </div>
    );
  };

  const loadSessions = async (uid: string) => {
    try {
      const userSessions = await pentestOrchestratorService.getUserSessions(uid);
      setSessions(userSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const startOrchestration = async () => {
    if (!target.trim() || !goal.trim()) {
      setError('Please provide both target and goal');
      return;
    }

    if (!userId) {
      setError('User not authenticated');
      return;
    }

    if (connectionError) {
      setError('API server is not running. Please start the API server first.');
      return;
    }

    setIsStarting(true);
    setError(null);
    
    // Clear previous output and prepare for real-time updates
    clearMessages();
    setLiveOutput({});
    setExecutingTools(new Set());
    setCompletedTools({});

    try {
      // Start the full orchestration process
      const session = await pentestOrchestratorService.executeFullOrchestration(
        target.trim(), 
        goal.trim(), 
        userId
      );

      setCurrentSession(session);
      setSessions(prev => [session, ...prev]);
      
      // Clear form
      setTarget('');
      setGoal('');
    } catch (error) {
      console.error('Error starting orchestration:', error);
      setError('Failed to start orchestration. Please try again.');
    } finally {
      setIsStarting(false);
    }
  };

  const loadSession = async (sessionId: string) => {
    try {
      const session = await pentestOrchestratorService.getSession(sessionId);
      if (session) {
        setCurrentSession(session);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      setError('Failed to load session');
    }
  };

  // Add helper for LLM output display
  const renderLLMOutput = (step: OrchestratorStep) => {
    if (!step.output) return null;
    const lines = step.output.split('\n').filter(Boolean);
    return (
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-3 mt-2">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-yellow-600">��</span>
          <span className="font-medium text-yellow-800">
            {step.tool === 'generate_wordlist' ? 'AI-Generated Wordlist' : 'AI-Generated Payloads'}
          </span>
          <button
            className="ml-auto text-xs text-blue-600 hover:underline"
            onClick={() => {
              navigator.clipboard.writeText(step.output);
            }}
          >Copy</button>
          <button
            className="ml-2 text-xs text-blue-600 hover:underline"
            onClick={() => {
              const blob = new Blob([step.output], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = step.tool === 'generate_wordlist' ? 'ai_wordlist.txt' : 'ai_payloads.txt';
              a.click();
              URL.revokeObjectURL(url);
            }}
          >Download</button>
        </div>
        <div className="max-h-48 overflow-y-auto text-xs font-mono bg-white border rounded p-2">
          {lines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading Pentest Orchestrator...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative overflow-x-hidden">
      {/* Navigation bar (copied from DashboardPage) */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-3">
              <div 
                onClick={() => navigate('/dashboard')}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="User" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main content below */}
      <div className="container mx-auto px-4 pt-[160px] relative pb-16">
        {/* Back to Dashboard Button */}
        <div className="mb-6">
          <Button 
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </Button>
        </div>
        {/* Manual Tool Test */}
        <div className="mb-8 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-lg shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-lg font-bold text-orange-800">Manual Tool Test</h2>
            <span className="text-xs text-gray-500">Test any tool directly before orchestration</span>
          </div>
          <form
            className="flex flex-wrap gap-2 items-center mb-2"
            onSubmit={async e => {
              e.preventDefault();
              setManualResult('');
              setManualError('');
              setManualLoading(true);
              try {
                const res = await fetch('/api/orchestrator/execute-tool', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ tool: manualTool, target: manualTarget })
                });
                const data = await res.json();
                if (data.success) {
                  setManualResult(data.raw_output || JSON.stringify(data.data, null, 2));
                } else {
                  setManualError(data.error || 'Tool execution failed');
                }
              } catch (err: any) {
                setManualError(err.message);
              } finally {
                setManualLoading(false);
              }
            }}
          >
            <select
              className="border rounded px-2 py-1"
              value={manualTool}
              onChange={e => {
                setManualTool(e.target.value);
                setManualResult('');
                setManualError('');
              }}
            >
              {allTools.map(tool => (
                <option key={tool} value={tool}>{tool}</option>
              ))}
            </select>
            <input
              className="border rounded px-2 py-1"
              value={manualTarget}
              onChange={e => setManualTarget(e.target.value)}
              placeholder="Target (e.g. example.com)"
            />
            <button
              type="submit"
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-1 rounded font-semibold disabled:opacity-50"
              disabled={manualLoading}
            >
              {manualLoading ? 'Running...' : 'Run Tool'}
            </button>
          </form>
          {manualResult && (
            <div className="mt-2 bg-white border border-orange-200 rounded p-3 text-xs max-h-64 overflow-y-auto">
              <strong>Output:</strong>
              {formatToolOutput(manualTool, manualResult)}
            </div>
          )}
          {manualError && (
            <div className="mt-2 bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
              <strong>Error:</strong> {manualError}
            </div>
          )}
        </div>

        {/* Dedicated Real-time Output Section */}
        {(executingTools.size > 0 || Object.keys(completedTools).length > 0) && (
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg font-bold text-blue-800">Live Tool Execution</h2>
              <Badge variant="secondary" className="text-sm">
                {executingTools.size} running, {Object.keys(completedTools).length} completed
              </Badge>
              <div className="flex-1"></div>
              <div className="text-sm text-blue-600">
                {new Date().toLocaleTimeString()}
              </div>
            </div>
            
            {/* Running Tools */}
            {executingTools.size > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-blue-700 mb-2">Running Tools</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Array.from(executingTools).map(tool => {
                    const output = liveOutput[tool] || '';
                    return (
                      <div key={tool} className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-sm font-bold text-gray-700 bg-blue-100 px-2 py-1 rounded">
                            {tool}
                          </span>
                          <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                          <span className="text-xs text-blue-600 font-medium">RUNNING</span>
                        </div>
                        <div className="bg-gray-50 p-3 rounded border max-h-32 overflow-y-auto">
                          {output ? formatToolOutput(tool, output) : <span className="text-gray-500 italic">Waiting for output...</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Completed Tools */}
            {Object.keys(completedTools).length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-green-700 mb-2">Completed Tools</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(completedTools).map(([tool, data]) => (
                    <div key={tool} className="bg-white p-4 rounded-lg border border-green-200 shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-bold text-gray-700 bg-green-100 px-2 py-1 rounded">
                          {tool}
                        </span>
                        <span className="text-xs text-green-600 font-medium">COMPLETED</span>
                        <span className="text-xs text-gray-500 ml-auto">
                          {Math.round(data.duration / 1000)}s
                        </span>
                      </div>
                      <div className="mb-2">
                        <span className="text-sm font-medium text-green-700">{data.summary}</span>
                      </div>
                      <details className="text-xs">
                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                          View full output ({data.output.split('\n').length} lines)
                        </summary>
                        <div className="mt-2 bg-gray-50 p-3 rounded border max-h-96 overflow-y-auto">
                          {formatToolOutput(tool, data.output)}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Orchestrator */}
          <div className="lg:col-span-2">
            {!currentSession ? (
              <Card>
                <CardHeader>
                  <CardTitle>Start New Orchestration</CardTitle>
                  <CardDescription>
                    Provide a target and goal, and let the AI orchestrator execute a dynamic pentesting plan.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  {connectionError && (
                    <Alert variant="destructive">
                      <AlertDescription>
                        API server is not running. To enable real-time updates and tool execution, start the API server:
                        <br />
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                          $env:ENABLE_REAL_TOOLS="true"; node server.js
                        </code>
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Target</label>
                    <Input
                      placeholder="e.g., example.com, 192.168.1.1"
                      value={target}
                      onChange={(e) => setTarget(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Goal</label>
                    <Textarea
                      placeholder="e.g., Find and validate critical vulnerabilities leading to RCE or PII exposure"
                      value={goal}
                      onChange={(e) => setGoal(e.target.value)}
                      rows={3}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Be specific! The AI will create a customized plan based on your goal.
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-blue-800">AI-Powered Orchestration</span>
                    </div>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Intelligent plan generation based on your goal</li>
                      <li>• Dynamic tool selection and sequencing</li>
                      <li>• AI-powered analysis of findings</li>
                      <li>• Goal achievement assessment</li>
                    </ul>
                  </div>
                  
                  <Button
                    onClick={startOrchestration}
                    disabled={isStarting || !target.trim() || !goal.trim() || !!connectionError}
                    className="w-full bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700"
                  >
                    {isStarting ? 'Starting Orchestration...' : connectionError ? 'API Server Required' : 'Start Orchestration'}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Active Orchestration</CardTitle>
                      <CardDescription>
                        Target: {currentSession.target} | Goal: {currentSession.goal}
                      </CardDescription>
                    </div>
                    <Badge variant={
                      currentSession.status === 'completed' ? 'default' :
                      currentSession.status === 'failed' ? 'destructive' :
                      'secondary'
                    }>
                      {currentSession.status.toUpperCase()}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Progress */}
                  <div className="mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Progress</span>
                      <span>{currentSession.steps.filter(s => s.status === 'completed').length} / {currentSession.steps.length}</span>
                    </div>
                    <Progress 
                      value={
                        currentSession.steps.length > 0 
                          ? (currentSession.steps.filter(s => s.status === 'completed').length / currentSession.steps.length) * 100
                          : 0
                      } 
                    />
                    {currentSession.status === 'executing' && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
                        <span>AI is analyzing and orchestrating...</span>
                        {executingTools.size > 0 && (
                          <span className="text-xs text-gray-500">
                            Running: {Array.from(executingTools).join(', ')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Global Live Output */}
                  {executingTools.size > 0 && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="font-medium text-blue-800">Live Tool Output</h3>
                        <Badge variant="secondary" className="text-xs">
                          {executingTools.size} tool{executingTools.size > 1 ? 's' : ''} running
                        </Badge>
                      </div>
                      <div className="space-y-3">
                        {Array.from(executingTools).map(tool => {
                          const output = liveOutput[tool] || '';
                          return (
                            <div key={tool} className="bg-white p-3 rounded border">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-medium text-gray-700">{tool}</span>
                                <div className="animate-spin w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                              </div>
                              {output && (
                                <div className="max-h-24 overflow-y-auto bg-gray-50 p-2 rounded">
                                  {formatToolOutput(tool, output)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Steps */}
                  <div className="space-y-4">
                    {currentSession.steps.map((step) => {
                      const isExecuting = executingTools.has(step.tool);
                      const liveToolOutput = liveOutput[step.tool] || '';
                      const isLLMStep = step.tool === 'generate_wordlist' || step.tool === 'generate_payloads';
                      const isAIDynamic = [
                        'scrape_content',
                        'extract_keywords',
                        'generate_wordlist',
                        'ffuf_fuzz',
                        'generate_payloads',
                        'recollapse_fuzz'
                      ].includes(step.tool);
                      return (
                        <div key={step.id} className={`border rounded-lg p-4 ${isAIDynamic ? 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200' : ''}`}>
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-medium flex items-center gap-2">
                              {step.title}
                            </h3>
                            <div className="flex items-center gap-2">
                              {isExecuting && (
                                <div className="flex items-center gap-1 text-blue-600">
                                  <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                                  <span className="text-sm">Running...</span>
                                </div>
                              )}
                              <Badge variant={
                                step.status === 'completed' ? 'default' :
                                step.status === 'failed' ? 'destructive' :
                                step.status === 'running' ? 'secondary' :
                                'outline'
                              }>
                                {step.status.toUpperCase()}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                          <p className="text-xs text-gray-500">Tool: {step.tool}</p>
                          {step.command && (
                            <p className="text-xs text-gray-500 mt-1">
                              Command: <code className="bg-gray-100 px-1 rounded">{step.command}</code>
                            </p>
                          )}
                          {/* Live Output */}
                          {isExecuting && (
                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-medium text-blue-800">Live Output</span>
                                <div className="animate-spin w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                              </div>
                              <div className="max-h-32 overflow-y-auto">
                                {liveToolOutput ? formatToolOutput(step.tool, liveToolOutput) : <span className="text-gray-500 italic">Waiting for output...</span>}
                              </div>
                            </div>
                          )}
                          {/* Final Output */}
                          {step.output && !isExecuting && !isLLMStep && (
                            <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                              <strong>Output:</strong>
                              <div className="mt-1">
                                {formatToolOutput(step.tool, step.output)}
                              </div>
                            </div>
                          )}
                          {/* LLM Output Special Card */}
                          {isLLMStep && step.output && !isExecuting && renderLLMOutput(step)}
                          {step.error && (
                            <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                              <strong>Error:</strong> {step.error}
                            </div>
                          )}
                          {step.duration && (
                            <p className="text-xs text-gray-500 mt-1">
                              Duration: {step.duration}ms
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Plan */}
                  {currentSession.plan && (
                    <div className="mt-6">
                      <h3 className="font-medium mb-2">Generated Plan</h3>
                      <pre className="bg-gray-50 p-4 rounded text-sm whitespace-pre-wrap">
                        {currentSession.plan}
                      </pre>
                    </div>
                  )}

                  {/* Findings */}
                  {currentSession.findings.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-medium mb-2">Findings</h3>
                      <ul className="space-y-1">
                        {currentSession.findings.map((finding, index) => (
                          <li key={index} className="text-sm text-gray-700">• {finding}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Vulnerabilities */}
                  {currentSession.vulnerabilities.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-medium mb-2">Vulnerabilities Found</h3>
                      <div className="space-y-2">
                        {currentSession.vulnerabilities.map((vuln, index) => (
                          <div key={index} className="border rounded p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{vuln.title}</h4>
                              <Badge variant={
                                vuln.severity === 'critical' ? 'destructive' :
                                vuln.severity === 'high' ? 'default' :
                                vuln.severity === 'medium' ? 'secondary' :
                                'outline'
                              }>
                                {vuln.severity.toUpperCase()}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">{vuln.description}</p>
                            <p className="text-xs text-gray-500">Evidence: {vuln.evidence}</p>
                            {vuln.url && (
                              <a href={vuln.url} target="_blank" rel="noopener noreferrer" 
                                 className="text-xs text-blue-600 hover:underline">
                                View URL
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="mt-6 flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentSession(null)}
                    >
                      Start New Session
                    </Button>
                    {currentSession.status === 'completed' && (
                      <Button
                        onClick={() => {
                          if (!currentSession) return;
                          // Build Markdown report
                          let md = `# Pentest Orchestrator Report\n\n`;
                          md += `**Target:** ${currentSession.target}\n\n`;
                          md += `**Goal:** ${currentSession.goal}\n\n`;
                          if (currentSession.plan) {
                            md += `## Plan\n\n
${currentSession.plan}\n\n`;
                          }
                          md += `## Steps\n\n`;
                          currentSession.steps.forEach((step, i) => {
                            md += `### ${i + 1}. ${step.title}\n`;
                            md += `- **Tool:** ${step.tool}\n`;
                            md += `- **Status:** ${step.status}\n`;
                            if (step.command) md += `- **Command:** \

	n${step.command}\n`;
                            if (step.output) {
                              md += `- **Output:**\n\n	n\n	n${step.output.split('\n').join('\n\t')}\n`;
                            }
                            if (step.error) md += `- **Error:** ${step.error}\n`;
                            md += '\n';
                          });
                          if (currentSession.findings.length > 0) {
                            md += `## Findings\n\n`;
                            currentSession.findings.forEach(f => {
                              md += `- ${f}\n`;
                            });
                            md += '\n';
                          }
                          if (currentSession.vulnerabilities.length > 0) {
                            md += `## Vulnerabilities\n\n`;
                            currentSession.vulnerabilities.forEach(vuln => {
                              md += `- **${vuln.title}** [${vuln.severity.toUpperCase()}]\n`;
                              md += `  - ${vuln.description}\n`;
                              if (vuln.evidence) md += `  - Evidence: ${vuln.evidence}\n`;
                              if (vuln.url) md += `  - URL: ${vuln.url}\n`;
                              if (vuln.cve) md += `  - CVE: ${vuln.cve}\n`;
                            });
                            md += '\n';
                          }
                          md += `---\n\n*Generated by RAWPA Pentest Orchestrator*\n`;
                          // Download as .md file
                          const blob = new Blob([md], { type: 'text/markdown' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `pentest-report-${currentSession.target.replace(/[^a-zA-Z0-9]/g, '_')}.md`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                      >
                        Generate Report
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* AI4EH Tools Info */}
            <Card>
              <CardHeader>
                <CardTitle>AI4EH Tools + Gemini</CardTitle>
                <CardDescription>Intelligent orchestration with AI-powered analysis</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs text-gray-600 mb-3">
                  The orchestrator uses Gemini AI to:
                  <ul className="mt-1 space-y-1">
                    <li>• Generate customized plans based on your goal</li>
                    <li>• Select optimal tool sequences</li>
                    <li>• Analyze findings intelligently</li>
                  </ul>
                </div>
                {pentestOrchestratorService.getAvailableTools().map((tool) => (
                  <div key={tool.name} className="flex items-center gap-2">
                    <Badge variant="outline">{tool.name}</Badge>
                    <span className="text-sm">{tool.description}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Session History */}
            {showHistory && (
              <Card>
                <CardHeader>
                  <CardTitle>Session History</CardTitle>
                  <CardDescription>Previous orchestration sessions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sessions.slice(1).map((session) => (
                      <div key={session.id} className="border rounded p-3 cursor-pointer hover:bg-gray-50"
                           onClick={() => loadSession(session.id)}>
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-sm">{session.target}</h4>
                          <Badge variant={
                            session.status === 'completed' ? 'default' :
                            session.status === 'failed' ? 'destructive' :
                            'secondary'
                          } className="text-xs">
                            {session.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-2">{session.goal}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(session.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                    {sessions.length <= 1 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No previous sessions found
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* New explainer card for AI-driven workflow */}
            <Card>
              <CardHeader>
                <CardTitle>Dynamic Exploit & Payload Crafting</CardTitle>
                <CardDescription>AI-driven workflow for advanced fuzzing and evasion</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-xs text-gray-700">
                <ul className="list-disc ml-4">
                  <li>Smart Content Discovery (scrape.py)</li>
                  <li>Keyword Extraction (nlp.py)</li>
                  <li>LLM Wordlist Generation (Gemini/OpenAI)</li>
                  <li>Fuzzing with ffuf (AI wordlist)</li>
                  <li>Context-Aware Payload Generation (Gemini/OpenAI)</li>
                  <li>Iterative WAF Evasion (recollapse)</li>
                </ul>
                <div className="mt-2 text-blue-700">
                  This workflow uses AI to generate highly targeted wordlists and payloads, enabling advanced fuzzing and WAF bypass techniques.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}; 